import React, { useState } from 'react';

const JobEmailBuilder = () => {
  const [file, setFile] = useState(null);
  const [jobTitle, setJobTitle] = useState('');
  const [targetCompany, setTargetCompany] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopyStatus(type);
    setTimeout(() => setCopyStatus(''), 2000);
  };

  const generateEmail = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setResult({
        subject: `Application for ${jobTitle || 'Software Engineer'} - Akhil`,
        body: `Dear Hiring Manager,\n\nI am writing to express my interest in the ${jobTitle || 'Software Engineer'} position at ${targetCompany || 'your company'}.\n\nWith my expertise in React and modern UI/UX principles, I am confident in my ability to contribute to your engineering team. I have attached my resume for your review and would appreciate the chance to discuss my background further.\n\nThank you for your time.\n\nBest regards,\nAkhil`,
        hrs: [
          { name: 'Sarah Jenkins', role: 'Senior Tech Recruiter', company: 'Google', email: 'sarah.j@example.com', initial: 'S' },
          { name: 'Mike Ross', role: 'Talent Acquisition Lead', company: 'Microsoft', email: 'mike.ross@example.com', initial: 'M' },
          { name: 'Jessica Pearson', role: 'Head of People', company: 'Amazon', email: 'jessica.p@example.com', initial: 'J' }
        ],
        shareLink: 'https://senvra.ai/share/email-result-akhil-782'
      });
    }, 1500);
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <main className="pt-[140px] pb-20 bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1300px] mx-auto px-5">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-4">
            Job Email <span className="text-primary">Builder</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto font-medium">
            Upload your resume to generate a tailored cold email and get a curated list of HR contacts.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 items-start">
          {/* Left Side - Input Section (Width Adjusted) */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm transition-all">
              <h3 className="flex items-center gap-3 text-lg font-black text-slate-900 dark:text-white mb-6">
                <span className="text-primary text-xl">📄</span> 1. Upload Resume
              </h3>
              
              {!file ? (
                <label className="cursor-pointer border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-10 flex flex-col items-center justify-center hover:border-primary transition-colors bg-slate-50/50 dark:bg-slate-800/30">
                  <input type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.txt" />
                  <div className="w-12 h-12 bg-white dark:bg-slate-900 rounded-xl shadow-sm flex items-center justify-center mb-4">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-400" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 font-bold text-sm text-center">
                    Click to upload PDF or TXT
                  </p>
                </label>
              ) : (
                <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/50 rounded-2xl p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-sm text-primary">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                    </div>
                    <div className="truncate max-w-[150px]">
                      <p className="text-sm font-black text-slate-700 dark:text-slate-200 truncate">{file.name}</p>
                      <p className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">File Selected</p>
                    </div>
                  </div>
                  <button onClick={resetUpload} className="text-slate-400 hover:text-red-500 transition-colors p-1">✕</button>
                </div>
              )}
            </div>

            {/* Job Details UI updated to match image_c5539d.png */}
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm">
              <h3 className="flex items-center gap-3 text-lg font-black text-slate-900 dark:text-white mb-6 uppercase tracking-tight">
                <span className="text-primary text-xl">👜</span> 2. Job Details (Optional)
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[1.5px] mb-3 ml-1">Target Job Title</label>
                  <div className="relative">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
                    </div>
                    <input 
                      type="text" 
                      placeholder="e.g. Senior React Developer" 
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl py-4.5 pl-14 pr-5 outline-none focus:border-primary transition-all font-bold text-slate-700 dark:text-white placeholder:font-normal"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[1.5px] mb-3 ml-1">Target Company</label>
                  <div className="relative">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M9 11h.01M15 11h.01M9 15h.01M15 15h.01"/></svg>
                    </div>
                    <input 
                      type="text" 
                      placeholder="e.g. Google (Leave empty for Top 10)" 
                      value={targetCompany}
                      onChange={(e) => setTargetCompany(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl py-4.5 pl-14 pr-5 outline-none focus:border-primary transition-all font-bold text-slate-700 dark:text-white placeholder:font-normal"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 mt-3 ml-1 font-medium italic">We will find HRs from top hiring companies.</p>
                </div>
              </div>
            </div>

            <button 
              onClick={generateEmail}
              disabled={!file || isGenerating}
              className={`w-full py-5 rounded-2xl font-black text-lg transition-all flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] ${
                !file ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed' : 'bg-[#0f172a] dark:bg-white text-white dark:text-slate-900 hover:opacity-90'
              }`}
            >
              {isGenerating ? 'Generating...' : <><span className="text-xl">✨</span> Generate Email & HR List</>}
            </button>
          </div>

          {/* Right Side - Results Section */}
          <div className="min-h-[600px]">
            {!result ? (
              <div className="h-full bg-white/50 dark:bg-slate-900/30 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[40px] flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 shadow-sm">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                </div>
                <h3 className="text-xl font-black text-slate-400 dark:text-slate-600 mb-2">Results will appear here</h3>
                <p className="text-slate-400 dark:text-slate-700 text-sm font-medium">Upload resume and click generate to see AI magic.</p>
              </div>
            ) : (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="flex justify-end">
                   <button onClick={() => handleCopy(result.shareLink, 'share')} className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-bold text-sm text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-50 transition">
                     <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>
                     {copyStatus === 'share' ? 'Copied!' : 'Share Result'}
                   </button>
                </div>

                {/* Generated Email Card */}
                <div className="bg-white dark:bg-slate-900 rounded-[32px] overflow-hidden border border-slate-100 dark:border-slate-800 shadow-xl">
                  <div className="bg-[#0f172a] p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                      <span className="text-white font-black text-sm uppercase tracking-wider">Generated Email</span>
                    </div>
                    <span className="bg-slate-800 text-[10px] text-slate-400 font-black px-3 py-1 rounded-lg">AI Draft</span>
                  </div>
                  <div className="p-8">
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Subject Line</label>
                        <button onClick={() => handleCopy(result.subject, 'sub')} className="text-primary font-bold text-xs hover:underline">{copyStatus === 'sub' ? 'Copied!' : 'Copy'}</button>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 font-bold text-slate-700 dark:text-slate-200">{result.subject}</div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Body</label>
                        <button onClick={() => handleCopy(result.body, 'body')} className="text-primary font-bold text-xs hover:underline">{copyStatus === 'body' ? 'Copied!' : 'Copy'}</button>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-xl border border-slate-100 dark:border-slate-800 font-medium text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed text-sm">
                        {result.body}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Hiring HR Contacts - Matches image_c5507d.png */}
                <div className="bg-white dark:bg-slate-900 rounded-[32px] overflow-hidden border border-slate-100 dark:border-slate-800 shadow-xl">
                  <div className="bg-primary p-5 flex items-center gap-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                    <span className="text-white font-black text-sm uppercase tracking-wider">Top Hiring HR Contacts</span>
                  </div>
                  <div className="divide-y divide-slate-50 dark:divide-slate-800">
                    {result.hrs.map((hr, i) => (
                      <div key={i} className="p-5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500 dark:text-slate-400">{hr.initial}</div>
                          <div>
                            <p className="text-sm font-black text-slate-900 dark:text-white">{hr.name}</p>
                            <p className="text-[11px] text-slate-400 font-medium">{hr.role} @ <span className="text-primary font-bold">{hr.company}</span></p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="hidden sm:flex bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-100 dark:border-slate-700 text-[11px] font-bold text-slate-400">
                            {hr.email}
                          </div>
                          <button onClick={() => handleCopy(hr.email, `hr-${i}`)} className="text-slate-300 hover:text-primary p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 transition-all">
                            {copyStatus === `hr-${i}` ? <span className="text-[10px] font-bold text-primary px-1">Copied!</span> : <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 text-center text-[10px] text-slate-400 italic">
                    * AI-generated suggestions. Please verify emails before sending.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default JobEmailBuilder;